import type { BuylistMatchItemPayload, BuylistReplaceItemPayload } from '../api/buylist';
import type { Product } from '../components/inventory/ProductSearchAutocomplete';

type CatalogPayload = Pick<
  BuylistReplaceItemPayload,
  'mtgjson_uuid' | 'tcgplayer_id' | 'product_line' | 'language'
>;

/**
 * Catalog identity for a buylist match or wrong-card replace.
 *
 * Magic search results use ``product_id`` as the MTGJSON uuid. Pokémon and
 * Lorcana results are TCGCSV rows: ``product_id`` is a TCGPlayer product id,
 * not an MTGJSON uuid.
 */
export function buylistCatalogFields(product: Product): CatalogPayload {
  if (product.catalog_source === 'tcgcsv') {
    const tcgplayerId = (product.tcgplayer_id || product.product_id || '').trim();
    return {
      tcgplayer_id: tcgplayerId,
      product_line: product.product_line,
      language: product.language,
    };
  }
  return { mtgjson_uuid: product.product_id };
}

function finishForCatalog(product: Product, finish: string): string {
  if (product.catalog_source !== 'tcgcsv') {
    return finish;
  }
  const available = product.available_finishes ?? [];
  if (available.length === 0 || available.includes(finish as (typeof available)[number])) {
    return finish;
  }
  if (
    (finish === 'etched' || finish === 'reverse_holofoil') &&
    available.includes('foil')
  ) {
    return 'foil';
  }
  return available[0];
}

export function buylistCatalogLinePayload(
  product: Product,
  details: { condition: string; finish: string; quantity: number },
): BuylistMatchItemPayload {
  return {
    ...buylistCatalogFields(product),
    card_name: product.name,
    set_code: product.set_code || undefined,
    card_number: product.card_number || undefined,
    condition: details.condition,
    finish: finishForCatalog(product, details.finish),
    quantity: details.quantity,
    image_url: product.image_url || undefined,
  };
}
